import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  UserGroupIcon,
  StarIcon,
  TrophyIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  EyeIcon,
  DocumentDuplicateIcon,
  ShieldCheckIcon,
  BoltIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CheckCircleIcon,
  PlayIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

const CopyTrading = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('6M');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const timeframes = ['1M', '3M', '6M', '1Y', 'All'];
  const categories = ['All', 'Crypto', 'Forex', 'Stocks', 'Commodities'];

  const topTraders = [
    {
      id: 1,
      name: 'Michael Chen',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
      verified: true,
      specialty: 'Crypto & DeFi',
      totalReturn: '+127.5%',
      monthlyReturn: '+18.3%',
      followers: 2847,
      totalTrades: 1243,
      winRate: 78.5,
      riskScore: 6.2,
      tradingExperience: '8 years',
      description: 'Specialized in cryptocurrency and DeFi protocols with focus on risk management.',
      recentTrades: [
        { symbol: 'BTC/USD', type: 'BUY', profit: '+12.4%', time: '2h ago' },
        { symbol: 'ETH/USD', type: 'SELL', profit: '+8.7%', time: '5h ago' },
        { symbol: 'SOL/USD', type: 'BUY', profit: '+15.2%', time: '1d ago' }
      ]
    },
    {
      id: 2,
      name: 'Sarah Martinez',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face',
      verified: true,
      specialty: 'Forex Trading',
      totalReturn: '+94.2%',
      monthlyReturn: '+12.1%',
      followers: 1923,
      totalTrades: 2156,
      winRate: 82.3,
      riskScore: 4.8,
      tradingExperience: '12 years',
      description: 'Conservative forex trader with consistent monthly returns and low drawdowns.',
      recentTrades: [
        { symbol: 'EUR/USD', type: 'SELL', profit: '+3.2%', time: '1h ago' },
        { symbol: 'GBP/JPY', type: 'BUY', profit: '+5.8%', time: '4h ago' },
        { symbol: 'USD/CAD', type: 'SELL', profit: '+2.1%', time: '6h ago' }
      ]
    },
    {
      id: 3,
      name: 'David Thompson',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
      verified: true,
      specialty: 'Growth Stocks',
      totalReturn: '+156.8%',
      monthlyReturn: '+22.7%',
      followers: 3421,
      totalTrades: 892,
      winRate: 75.2,
      riskScore: 7.1,
      tradingExperience: '15 years',
      description: 'Focuses on high-growth technology stocks and emerging market opportunities.',
      recentTrades: [
        { symbol: 'TSLA', type: 'BUY', profit: '+18.9%', time: '3h ago' },
        { symbol: 'NVDA', type: 'SELL', profit: '+25.4%', time: '1d ago' },
        { symbol: 'AMD', type: 'BUY', profit: '+12.3%', time: '2d ago' }
      ]
    },
    {
      id: 4,
      name: 'Emma Rodriguez',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
      verified: true,
      specialty: 'Options Trading',
      totalReturn: '+203.4%',
      monthlyReturn: '+31.2%',
      followers: 1876,
      totalTrades: 1567,
      winRate: 71.8,
      riskScore: 8.3,
      tradingExperience: '10 years',
      description: 'Expert in options strategies with focus on high-probability setups.',
      recentTrades: [
        { symbol: 'SPY Call', type: 'BUY', profit: '+45.2%', time: '2h ago' },
        { symbol: 'QQQ Put', type: 'SELL', profit: '+67.8%', time: '1d ago' },
        { symbol: 'AAPL Call', type: 'BUY', profit: '+23.1%', time: '2d ago' }
      ]
    }
  ];

  const features = [
    {
      icon: DocumentDuplicateIcon,
      title: 'Automatic Copy Trading',
      description: 'Automatically replicate trades from successful traders in real-time with customizable allocation settings.'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Risk Management',
      description: 'Advanced risk controls including stop-loss, take-profit, and maximum allocation limits.'
    },
    {
      icon: BoltIcon,
      title: 'Instant Execution',
      description: 'Lightning-fast trade copying with minimal latency to maximize profit opportunities.'
    },
    {
      icon: ChartBarIcon,
      title: 'Performance Analytics',
      description: 'Detailed performance tracking and analytics for both traders and copiers.'
    },
    {
      icon: CurrencyDollarIcon,
      title: 'Flexible Allocation',
      description: 'Copy multiple traders with custom allocation percentages and investment amounts.'
    },
    {
      icon: ClockIcon,
      title: '24/7 Monitoring',
      description: 'Round-the-clock monitoring and execution across global markets and time zones.'
    }
  ];

  const stats = [
    { label: 'Active Copy Traders', value: '12,500+', icon: UserGroupIcon },
    { label: 'Total Trades Copied', value: '2.8M+', icon: DocumentDuplicateIcon },
    { label: 'Average Monthly Return', value: '+15.7%', icon: ArrowTrendingUpIcon },
    { label: 'Success Rate', value: '89.3%', icon: TrophyIcon }
  ];

  return (
    <div className="min-h-screen bg-white w-full overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative py-20 sm:py-32 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 overflow-hidden mt-20">
        <div className="absolute inset-0">
          {/* Background Elements */}
          <div className="absolute inset-0 opacity-10">
            <div className="grid grid-cols-8 h-full">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="border-r border-white/20 h-full" />
              ))}
            </div>
          </div>
          
          {/* Floating Elements */}
          {[...Array(12)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-blue-300/30 rounded-full"
              style={{
                left: `${10 + Math.random() * 80}%`,
                top: `${10 + Math.random() * 80}%`,
              }}
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.3, 0.7, 0.3],
              }}
              transition={{
                duration: 4 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                <span className="block">Copy Trading</span>
                <span className="block text-transparent bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text">
                  Made Simple
                </span>
              </h1>
              
              <p className="text-lg sm:text-xl text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed">
                Follow and automatically copy trades from the world's most successful traders. 
                Start earning with proven strategies and expert insights.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <motion.button 
                  whileHover={{ scale: 1.02 }} 
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-xl shadow-xl transition-all duration-300"
                >
                  Start Copy Trading
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </motion.button>
                <motion.button 
                  whileHover={{ scale: 1.02 }} 
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center justify-center px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl border border-white/20 backdrop-blur-sm transition-all duration-300"
                >
                  <PlayIcon className="mr-2 h-5 w-5" />
                  How It Works
                </motion.button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="flex justify-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                    <stat.icon className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Top Traders Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Top Performing Traders
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Discover and copy trades from our most successful traders with verified track records.
            </p>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <div className="flex gap-2">
              {timeframes.map((timeframe) => (
                <button
                  key={timeframe}
                  onClick={() => setSelectedTimeframe(timeframe)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedTimeframe === timeframe
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {timeframe}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedCategory === category
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Trader Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {topTraders.map((trader, index) => (
              <motion.div
                key={trader.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-shadow"
              >
                {/* Trader Header */}
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center">
                    <img
                      src={trader.avatar}
                      alt={trader.name}
                      className="w-16 h-16 rounded-full object-cover mr-4"
                    />
                    <div>
                      <div className="flex items-center">
                        <h3 className="text-xl font-semibold text-gray-900">{trader.name}</h3>
                        {trader.verified && (
                          <CheckCircleIcon className="h-5 w-5 text-blue-500 ml-2" />
                        )}
                      </div>
                      <p className="text-gray-600">{trader.specialty}</p>
                      <p className="text-sm text-gray-500">{trader.tradingExperience} experience</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-green-600">{trader.totalReturn}</div>
                    <div className="text-sm text-gray-500">Total Return</div>
                  </div>
                </div>

                {/* Performance Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-900">{trader.monthlyReturn}</div>
                    <div className="text-xs text-gray-500">Monthly</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-900">{trader.winRate}%</div>
                    <div className="text-xs text-gray-500">Win Rate</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-900">{trader.followers}</div>
                    <div className="text-xs text-gray-500">Followers</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-900">{trader.riskScore}/10</div>
                    <div className="text-xs text-gray-500">Risk Score</div>
                  </div>
                </div>

                {/* Description */}
                <p className="text-gray-600 mb-6">{trader.description}</p>

                {/* Recent Trades */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Recent Trades</h4>
                  <div className="space-y-2">
                    {trader.recentTrades.map((trade, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center">
                          <span className="text-sm font-medium text-gray-900">{trade.symbol}</span>
                          <span className={`ml-2 px-2 py-1 text-xs rounded ${
                            trade.type === 'BUY' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {trade.type}
                          </span>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-semibold text-green-600">{trade.profit}</div>
                          <div className="text-xs text-gray-500">{trade.time}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Copy Button */}
                <button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-3 rounded-xl transition-all duration-300">
                  Copy Trader
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Copy Trading?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Advanced features designed to maximize your copy trading success.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Start Copy Trading?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Join thousands of successful copy traders and start earning with proven strategies today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-xl hover:bg-gray-50 transition-colors">
                Get Started Now
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </button>
              <button className="inline-flex items-center justify-center px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl border border-white/20 backdrop-blur-sm transition-all duration-300">
                Learn More
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CopyTrading; 